import { AxiosError } from "axios";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  differenceInMonths
} from "date-fns";
import {
  CreateRevenueItemRequest,
  UpdateBundleLine
} from "@/pages/contracts/hooks";
import { ContractRevenueItem } from "@/pages/contracts/types";
import {
  bundleLineTotal,
  bundleableTerms,
  parseBundleQuantity
} from "@/pages/settings/pages/products-v2/bundleUtils";
import {
  ChargeType,
  Pricing,
  PricingModel,
  ProductBundleItem,
  ServiceItem
} from "@/pages/settings/pages/products-v2/types";
import { toDateOnlyISO, toLocalDate } from "@/utils/datetime";
import { bundlePricingLineTotal } from "./bundlePricing";
import { getServiceDurationInMonths, monthsBetween } from "./consts";
import { serviceEndDateAfterMonths } from "./utils";

// A configured service's default quantity and term, in Settings or on a deal.
export type BundleItemDefaults = Pick<
  ProductBundleItem,
  "serviceItemId" | "serviceItemPricingId" | "quantity"
>;

export interface BundleServiceOption<
  Item extends BundleItemDefaults = ProductBundleItem
> {
  service: ServiceItem;
  // The service's default quantity and term in the bundle.
  item: Item;
}

// The bundle's services a deal can take: a service since deleted, or one whose
// configured term can no longer be bundled, has nothing the server would accept.
export const bundleServiceOptions = <Item extends BundleItemDefaults>(
  bundle: { items: Item[] },
  services: ServiceItem[]
): BundleServiceOption<Item>[] =>
  bundle.items.flatMap((item) => {
    const service = services.find(({ id }) => id === item.serviceItemId);
    const hasTerm =
      service &&
      bundleableTerms(service).some(
        ({ id }) => id === item.serviceItemPricingId
      );
    return service && hasTerm ? [{ service, item }] : [];
  });

// A one-time charge covers a single month, as in the Add service drawer.
const lineEndDate = (service: ServiceItem, term: Pricing, startDate: string) =>
  serviceEndDateAfterMonths(
    startDate,
    service.chargeType === ChargeType.ONE_TIME
      ? 1
      : getServiceDurationInMonths(term.serviceItemPeriods)
  );

// One line of the bundle, built the way the Add service drawer builds a new
// standalone line on the same term: dates from the bundle's start date, no
// discount, and the catalog price.
export const bundleLineRequest = (
  service: ServiceItem,
  term: Pricing,
  quantity: number,
  startDate: string
): CreateRevenueItemRequest => {
  const isFlatRate = service.pricingModel === PricingModel.FLAT_RATE;
  return {
    serviceItemId: service.id,
    serviceItemName: service.name,
    serviceItemPricingId: term.id,
    serviceStartDate: startDate,
    serviceEndDate: lineEndDate(service, term, startDate),
    autoRenewal: false,
    quantity: isFlatRate ? null : quantity,
    pricePerUnit: isFlatRate ? null : (term.price ?? 0),
    isDiscountPercentage: true,
    discountAmount: 0,
    totalLocal: bundleLineTotal(
      service.pricingModel,
      service.chargeType,
      term,
      quantity
    ),
    billingStartDate: startDate,
    billingEndDate: null,
    customBillingFrequency: term.billingFrequency,
    customServiceDuration: term.serviceItemPeriods,
    customOptOutDate: null,
    eqPaymentsNumOfPayments: null,
    eqPaymentsBillingFrequency: null,
    scheduledPayments: []
  };
};

const dateOnly = (value: string | null | undefined) =>
  value?.slice(0, 10) ?? "";

interface BundleDates {
  startDate: string;
  previousStartDate: string;
}

// A line moves with the bundle's start date when it follows it and is still
// on the old date; a line with its own date, or one that renewed onto a later
// period, keeps its dates.
const shiftsWithBundle = (
  line: ContractRevenueItem,
  { startDate, previousStartDate }: BundleDates
) =>
  startDate !== previousStartDate &&
  line.followsBundleStartDate !== false &&
  dateOnly(line.serviceStartDate) === previousStartDate;

// A date as far from the new start as it was from the old one, in months and
// then days, so a date on the billing cycle (the 1st, say) stays on it.
const moveWithStart = (
  value: string,
  { startDate, previousStartDate }: BundleDates
) => {
  const from = toLocalDate(previousStartDate);
  const date = toLocalDate(dateOnly(value));
  const months = differenceInMonths(date, from);
  const days = differenceInCalendarDays(date, addMonths(from, months));
  return toDateOnlyISO(
    addDays(addMonths(toLocalDate(startDate), months), days)
  );
};

// A moved line's end: a line that runs whole months ends that many months
// from its new start, as a term's end is worked out everywhere else; any other
// end keeps the line's length in days.
const movedEndDate = (
  end: string,
  { startDate, previousStartDate }: BundleDates
) => {
  const months = monthsBetween(previousStartDate, end);
  if (serviceEndDateAfterMonths(previousStartDate, months) === dateOnly(end)) {
    return serviceEndDateAfterMonths(startDate, months);
  }
  const days = differenceInCalendarDays(
    toLocalDate(startDate),
    toLocalDate(previousStartDate)
  );
  return toDateOnlyISO(addDays(toLocalDate(dateOnly(end)), days));
};

// A moved line keeps its length, its billing dates and its scheduled payments
// where they were in its period, amounts as entered: the server never moves
// dates, so every one of them is sent moved.
const shiftedDates = (
  line: ContractRevenueItem,
  dates: BundleDates
): Partial<UpdateBundleLine> => ({
  serviceStartDate: dates.startDate,
  serviceEndDate: line.serviceEndDate
    ? movedEndDate(line.serviceEndDate, dates)
    : line.serviceEndDate,
  billingStartDate: line.billingStartDate
    ? moveWithStart(line.billingStartDate, dates)
    : line.billingStartDate,
  billingEndDate: line.billingEndDate
    ? movedEndDate(line.billingEndDate, dates)
    : line.billingEndDate,
  // Sending a schedule replaces it, so one is sent only when there is one.
  ...(line.scheduledPayments?.length
    ? {
        scheduledPayments: line.scheduledPayments.map((payment) => ({
          paymentNumber: payment.paymentNumber,
          billingDate: moveWithStart(payment.billingDate, dates),
          amount: payment.amount,
          percentage: payment.percentage
        }))
      }
    : {})
});

const isRepriced = (
  service: ServiceItem,
  term: Pricing,
  quantity: number,
  line: ContractRevenueItem
) =>
  term.id !== line.serviceItemPricingId ||
  // A flat-rate line is priced once, whatever its quantity.
  (service.pricingModel !== PricingModel.FLAT_RATE &&
    quantity !== line.quantity);

// A bundle line's own total, before its share of the bundle's discount: the
// stored total has that share taken off, in the same currency as the total.
export const lineTotalBeforeBundleDiscount = (
  line: ContractRevenueItem
): number => {
  if (line.totalLocal != null) {
    return line.totalLocal + (line.bundleDiscountLocal ?? 0);
  }
  if (line.total != null) return line.total + (line.bundleDiscount ?? 0);
  return 0;
};

// A bundle line as the table shows it: its total in each currency with its
// share of the bundle's discount added back, so its price and total are its
// own; the discount shows on the bundle.
export const lineWithoutBundleDiscount = (
  line: ContractRevenueItem
): ContractRevenueItem => ({
  ...line,
  total: line.total == null ? null : line.total + (line.bundleDiscount ?? 0),
  totalLocal:
    line.totalLocal == null
      ? null
      : line.totalLocal + (line.bundleDiscountLocal ?? 0)
});

// The server's refusal of a bundle discount larger than the bundle total; any
// other 400 (a service the bundle no longer offers, say) is a save error. The
// refusal carries no code, only contractsapi's detail text ("The discount can't
// be more than the bundle total", bundles/rules.py), so a rewording there must
// change this prefix too.
export const isBundleDiscountRefused = (error: unknown): boolean => {
  const response = (error as AxiosError<{ detail?: unknown }>)?.response;
  return (
    response?.status === 400 &&
    typeof response.data?.detail === "string" &&
    response.data.detail.startsWith("The discount can't be more")
  );
};

// What a bundle's existing line totals with the drawer's quantity and term,
// before the bundle's discount: as stored when neither changed, otherwise
// repriced with its own discount kept.
export const existingLineTotal = (
  service: ServiceItem,
  term: Pricing,
  quantity: number,
  line: ContractRevenueItem
): number => {
  if (!isRepriced(service, term, quantity, line)) {
    return lineTotalBeforeBundleDiscount(line);
  }
  const discount = {
    isDiscountPercentage: line.isDiscountPercentage,
    discountAmount: line.discountAmount
  };
  if (term.id !== line.serviceItemPricingId) {
    return bundlePricingLineTotal({
      pricingModel: service.pricingModel,
      chargeType: service.chargeType,
      price: term.price,
      quantity,
      period: term.serviceItemPeriods,
      ...discount
    });
  }
  // On its own term a line runs for the length it was recorded with, as Edit
  // service prices it, not the catalog term's.
  return bundlePricingLineTotal({
    pricingModel: service.pricingModel,
    chargeType: service.chargeType,
    price: line.pricePerUnit ?? term.price,
    quantity,
    period: line.customServiceDuration ?? term.serviceItemPeriods,
    startDate: line.serviceStartDate,
    endDate: line.serviceEndDate,
    ...discount
  });
};

// An existing line as the bundle save sends it: only what changed, since the
// server keeps every field a line is sent without.
export const bundleUpdateLine = (
  service: ServiceItem,
  term: Pricing,
  quantity: number,
  line: ContractRevenueItem,
  dates: BundleDates
): UpdateBundleLine => {
  const termChanged = term.id !== line.serviceItemPricingId;
  const update: UpdateBundleLine = {
    id: line.id,
    serviceItemId: service.id,
    ...(shiftsWithBundle(line, dates) ? shiftedDates(line, dates) : {})
  };
  // A new term sets the line's length from its own start.
  if (termChanged) {
    const start = update.serviceStartDate ?? dateOnly(line.serviceStartDate);
    if (start) update.serviceEndDate = lineEndDate(service, term, start);
    update.serviceItemPricingId = term.id;
    update.customBillingFrequency = term.billingFrequency;
    update.customServiceDuration = term.serviceItemPeriods;
    // A billing end follows the new term's end; the server keeps a sent null
    // as the stored value, so it can't be cleared from here.
    if (update.billingEndDate ?? line.billingEndDate) {
      update.billingEndDate = update.serviceEndDate;
    }
  }
  if (isRepriced(service, term, quantity, line)) {
    // A flat-rate line has no quantity or unit price to change.
    if (service.pricingModel !== PricingModel.FLAT_RATE) {
      update.quantity = quantity;
      update.pricePerUnit = termChanged
        ? (term.price ?? 0)
        : (line.pricePerUnit ?? term.price ?? 0);
    }
    update.totalLocal = existingLineTotal(service, term, quantity, line);
  }
  return update;
};

// A line the drawer can't edit, sent as it is; it still moves with the
// bundle's start date. Every bundle line has one of its configured services.
const fixedLineUpdate = (
  line: ContractRevenueItem,
  dates: BundleDates
): UpdateBundleLine[] =>
  line.serviceItemId
    ? [
        {
          id: line.id,
          serviceItemId: line.serviceItemId,
          ...(shiftsWithBundle(line, dates) ? shiftedDates(line, dates) : {})
        }
      ]
    : [];

// A bundle's lines split into the one the drawer edits for each service it
// lists and the lines it can't edit: a service it doesn't list (gone from the
// catalog, or its bundle term no longer bundleable), a term the drawer can't
// offer, a quantity the drawer can't take (zero or a downsell), or a second
// line of the same service.
export const splitBundleLines = (
  lines: ContractRevenueItem[],
  listed: ServiceItem[]
) => {
  const editable = new Map<string, ContractRevenueItem>();
  const fixed: ContractRevenueItem[] = [];
  lines.forEach((line) => {
    const service = listed.find(({ id }) => id === line.serviceItemId);
    const hasTerm =
      !!service &&
      bundleableTerms(service).some(
        ({ id }) => id === line.serviceItemPricingId
      );
    const hasQuantity =
      line.quantity == null ||
      parseBundleQuantity(String(line.quantity)) !== null;
    if (service && hasTerm && hasQuantity && !editable.has(service.id)) {
      editable.set(service.id, line);
    } else {
      fixed.push(line);
    }
  });
  return { editable, fixed };
};

export interface CheckedBundleService {
  service: ServiceItem;
  term: Pricing;
  quantity: number;
}

// The full set of lines the bundle save sends: a checked service with a line
// updates it, one without adds a line, and an unchecked one is left out, which
// deletes its line. Lines the drawer can't edit are kept.
export const bundleUpdateLines = (
  checked: CheckedBundleService[],
  { editable, fixed }: ReturnType<typeof splitBundleLines>,
  dates: BundleDates
): UpdateBundleLine[] => [
  ...checked.map(({ service, term, quantity }) => {
    const line = editable.get(service.id);
    return line
      ? bundleUpdateLine(service, term, quantity, line, dates)
      : bundleLineRequest(service, term, quantity, dates.startDate);
  }),
  ...fixed.flatMap((line) => fixedLineUpdate(line, dates))
];
